import type { LabConfig, TestResult } from '../types';
import { computeStats, now } from '../utils';

const GROUP = 'E_workers';

// Inline worker code that loads WASM and executes fixed-work or atomic-contention tasks.
// The worker expects an initial 'init' message with { type: 'init', wasmUrl: string }.
const WASM_WORKER_CODE = `
let wasm = null;

async function initWasm(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch ' + url);
  const { instance } = await WebAssembly.instantiateStreaming(response, {
    env: { abort: () => { throw new Error('WASM abort'); } },
  });
  wasm = instance.exports;
}

self.onmessage = async function(e) {
  const data = e.data;

  if (data.type === 'init') {
    try {
      await initWasm(data.wasmUrl);
      self.postMessage({ type: 'ready' });
    } catch (err) {
      self.postMessage({ type: 'error', error: err.message });
    }
    return;
  }

  if (!wasm) {
    self.postMessage({ type: 'error', error: 'WASM not initialized' });
    return;
  }

  if (data.type === 'fixedWork') {
    const { iterations, requestId } = data;
    const result = wasm.fixedWork(iterations);
    self.postMessage({ type: 'fixedWork', requestId, result });
    return;
  }

  if (data.type === 'atomicContention') {
    const { sab, iterations, workerId } = data;
    const view = new Int32Array(sab);
    const t0 = performance.now();
    for (let i = 0; i < iterations; i++) {
      Atomics.add(view, 0, 1);
    }
    const t1 = performance.now();
    self.postMessage({ type: 'atomicContention', workerId, durationMs: t1 - t0 });
    return;
  }

  if (data.type === 'echo') {
    self.postMessage(data);
  }
};
`;

function workerUrl(code: string): string {
  return URL.createObjectURL(new Blob([code], { type: 'application/javascript' }));
}

interface WorkerPool {
  url: string;
  workers: Worker[];
  creationMs: number;
  ready: Promise<void>;
}

async function createWorkerPool(count: number, wasmUrl: string): Promise<WorkerPool> {
  const url = workerUrl(WASM_WORKER_CODE);
  const t0 = now();
  const workers: Worker[] = [];
  const readies: Promise<void>[] = [];

  for (let i = 0; i < count; i++) {
    const w = new Worker(url);
    workers.push(w);
    w.postMessage({ type: 'init', wasmUrl });
    readies.push(
      new Promise<void>((resolve, reject) => {
        function onMessage(e: MessageEvent) {
          if (e.data.type === 'ready') {
            cleanup();
            resolve();
          } else if (e.data.type === 'error') {
            cleanup();
            reject(new Error(e.data.error));
          }
        }
        function onError(err: ErrorEvent) {
          cleanup();
          reject(err);
        }
        function cleanup() {
          w.removeEventListener('message', onMessage);
          w.removeEventListener('error', onError);
        }
        w.addEventListener('message', onMessage);
        w.addEventListener('error', onError);
      })
    );
  }

  const creationMs = now() - t0;
  const ready = Promise.all(readies).then(() => {});
  return { url, workers, creationMs, ready };
}

function terminatePool(pool: WorkerPool): void {
  pool.workers.forEach((w) => w.terminate());
  URL.revokeObjectURL(pool.url);
}

function runFixedWorkOnPool(pool: WorkerPool, iterations: number): Promise<number> {
  return pool.ready.then(() => {
    const workers = pool.workers;
    const t0 = now();
    let finished = 0;
    let requestId = 0;

    return new Promise<number>((resolve, reject) => {
      function onMessage(e: MessageEvent) {
        if (e.data.type === 'fixedWork') {
          finished++;
          if (finished === workers.length) {
            resolve(now() - t0);
          }
        } else if (e.data.type === 'error') {
          reject(new Error(e.data.error));
        }
      }

      for (const w of workers) {
        w.addEventListener('message', onMessage);
        w.addEventListener('error', reject as EventListener);
        w.postMessage({ type: 'fixedWork', iterations, requestId: requestId++ });
      }
    });
  });
}

async function measureWorkerOverhead(pool: WorkerPool, samples: number): Promise<number[]> {
  await pool.ready;
  const w = pool.workers[0];
  const raw: number[] = [];
  let requestId = 0;

  return new Promise<number[]>((resolve, reject) => {
    function onMessage(e: MessageEvent) {
      if (e.data.type === 'error') {
        reject(new Error(e.data.error));
        return;
      }
      if (e.data.type !== 'echo') return;
      const received = now();
      const sent = e.data.sent;
      raw.push(received - sent);
      if (raw.length < samples) {
        requestId++;
        w.postMessage({ type: 'echo', sent: now(), requestId });
      } else {
        resolve(raw);
      }
    }

    w.addEventListener('message', onMessage);
    w.addEventListener('error', reject as EventListener);
    w.postMessage({ type: 'echo', sent: now(), requestId });
  });
}

async function measureAtomicContention(
  pool: WorkerPool,
  workerCount: number,
  iterations: number,
  runs: number
): Promise<number[]> {
  await pool.ready;
  const sab = new SharedArrayBuffer(4);
  const samples: number[] = [];

  for (let r = 0; r < runs; r++) {
    Atomics.store(new Int32Array(sab), 0, 0);
    let finished = 0;
    const perWorkerResults: number[] = [];

    await new Promise<void>((resolve, reject) => {
      function onMessage(e: MessageEvent) {
        if (e.data.type === 'error') {
          reject(new Error(e.data.error));
          return;
        }
        if (e.data.type !== 'atomicContention') return;
        perWorkerResults.push(e.data.durationMs);
        finished++;
        if (finished === workerCount) resolve();
      }

      for (let i = 0; i < workerCount; i++) {
        const w = pool.workers[i];
        w.addEventListener('message', onMessage);
        w.addEventListener('error', reject as EventListener);
        w.postMessage({ type: 'atomicContention', sab, iterations, workerId: i });
      }
    });

    samples.push(perWorkerResults.reduce((a, b) => a + b, 0) / perWorkerResults.length);
  }

  return samples;
}

export async function runWorkerTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];

  if (!config.enableWorkers) {
    tests.push({
      name: 'workers_disabled',
      group: GROUP,
      success: false,
      durationMs: 0,
      error: 'Workers disabled in config',
    });
    return tests;
  }

  if (!window.Worker) {
    tests.push({
      name: 'workers_available',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: 'Web Workers not supported',
    });
    return tests;
  }

  const isProd = config.mode === 'prod';
  const maxWorkers = isProd ? Math.min(4, navigator.hardwareConcurrency || 2) : 4;
  const scalingRuns = config.workerScalingRuns ?? (isProd ? 2 : 3);
  const scalingIterations = config.workerScalingIterations ?? (isProd ? 50_000 : 150_000);
  const overheadSamples = config.workerOverheadSamples ?? (isProd ? 20 : 40);
  const contentionWorkers = 4;
  const contentionIterations = config.workerContentionIterations ?? (isProd ? 10_000 : 30_000);
  const contentionRuns = config.workerContentionRuns ?? (isProd ? 2 : 3);

  const wasmUrl = new URL('/worker.wasm', location.href).href;

  let pool: WorkerPool | null = null;
  try {
    pool = await createWorkerPool(maxWorkers, wasmUrl);
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    for (const name of ['core_scaling', 'worker_overhead', 'atomic_contention']) {
      tests.push({
        name,
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: `Worker WASM load failed: ${error}`,
      });
    }
    tests.push({
      name: 'worker_creation_overhead',
      group: GROUP,
      success: true,
      durationMs: 0,
      metadata: { totalWorkerCreationMs: 0 },
    });
    return tests;
  }

  try {
    const scaling: Record<number, number[]> = {};
    const threadCounts = Array.from({ length: maxWorkers }, (_, i) => i + 1);

    for (const count of threadCounts) {
      scaling[count] = [];
      for (let i = 0; i < scalingRuns; i++) {
        // Use only the first 'count' workers from the shared pool.
        const subPool: WorkerPool = {
          ...pool,
          workers: pool.workers.slice(0, count),
        };
        scaling[count].push(await runFixedWorkOnPool(subPool, scalingIterations));
      }
    }

    const statsByCount = Object.fromEntries(
      Object.entries(scaling).map(([k, v]) => [k, computeStats(v)])
    );

    const one = statsByCount[1]?.median ?? 1;
    const n = statsByCount[maxWorkers]?.median ?? one;
    const two = statsByCount[2]?.median ?? one;

    tests.push({
      name: 'core_scaling',
      group: GROUP,
      success: true,
      durationMs: Object.values(scaling).flat().reduce((a, b) => a + b, 0),
      metadata: {
        scaling: statsByCount,
        iterations: scalingIterations,
        runs: scalingRuns,
      },
      ratios: {
        speedup_2_to_1: one / two,
        speedup_n_to_1: one / n,
        efficiency_n: (one / n) / maxWorkers,
      },
    });
  } catch (err) {
    tests.push({
      name: 'core_scaling',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const raw = await measureWorkerOverhead(pool, overheadSamples);
    const stats = computeStats(raw);

    tests.push({
      name: 'worker_overhead',
      group: GROUP,
      success: true,
      durationMs: raw.reduce((a, b) => a + b, 0),
      samples: raw,
      stats,
      metadata: {
        overheadPerWorkerMs: stats.median,
      },
    });
  } catch (err) {
    tests.push({
      name: 'worker_overhead',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = await measureAtomicContention(
      pool,
      contentionWorkers,
      contentionIterations,
      contentionRuns
    );
    const stats = computeStats(samples);

    tests.push({
      name: 'atomic_contention',
      group: GROUP,
      success: true,
      durationMs: samples.reduce((a, b) => a + b, 0),
      samples,
      stats,
      metadata: {
        iterations: contentionIterations,
        workers: contentionWorkers,
        runs: contentionRuns,
      },
      ratios: {
        time_per_atomic_op_ns: (stats.median / contentionIterations) * 1_000_000,
      },
    });
  } catch (err) {
    tests.push({
      name: 'atomic_contention',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  tests.push({
    name: 'worker_creation_overhead',
    group: GROUP,
    success: true,
    durationMs: pool.creationMs,
    metadata: {
      totalWorkerCreationMs: pool.creationMs,
      note: 'Single shared worker pool created once. Work measurements exclude this.',
    },
  });

  terminatePool(pool);
  return tests;
}
